import React from 'react';
import { Routes, Route } from 'react-router-dom';
import ProtectedRoute from 'src/routes/ProtectedRoute';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import { getInsightRoutesAvailability } from 'src/routes/state/routesSelector';
import { useAppSelector } from 'src/store';
import { IndividualRouteSetting, EcologyHubViews } from 'src/routes/routesConfig';
import { Resources } from 'src/shared/types';
import useFeature from 'src/shared/hooks/useFeature';
import useProject from 'src/shared/hooks/useProject';
import Loader from 'src/shared/components/loader/Loader';
import useHabitatInsightsAccess from 'src/shared/hooks/useHabitatInsightsAccess';
import { useProductAccess } from 'src/shared/hooks/useProductLicensing';
import { ProductKey } from 'src/shared/types/engine/products';
import AccessGate from 'src/shared/components/product-gate/AccessGate';
import CommunityComparison from './views/community-comparison/CommunityComparison';
import SpeciesTrends from './views/species-trends/SpeciesTrends';
import StatisticalComparison from './views/statistical-comparison/StatisticalComparison';
import TaxonomicComposition from './views/taxonomic-composition/TaxonomicComposition';
import SampleComparison from './views/sample-comparison/SampleComparison';
import HabitatTrends from './views/habitat-trends/HabitatTrends';
import FreemiumEcologyHub from './views/freemium-ecology-hub/FreemiumEcologyHub';

export const insightRouteSettings: { [key in EcologyHubViews]: IndividualRouteSetting } = {
    speciesTrends: {
        name: 'Species Trends',
        path: '/species-trends',
        handler: <SpeciesTrends />,
        resource: Resources.SPECIES_TRENDS,
    },
    habitatTrends: {
        name: 'Habitat Trends',
        path: '/habitat-trends',
        handler: <HabitatTrends />,
        resource: Resources.HABITAT_TRENDS,
    },
    sampleComparison: {
        name: 'Sample Comparison',
        path: '/sample-comparison',
        handler: <SampleComparison />,
        resource: Resources.SAMPLE_COMPARISON,
    },
    statisticalComparison: {
        name: 'Statistical Comparison',
        path: '/statistical-comparison',
        handler: <StatisticalComparison />,
        resource: Resources.STATISTICAL_COMPARISON,
    },
    taxonomicComposition: {
        name: 'Taxonomic Composition',
        path: '/taxonomic-composition',
        handler: <TaxonomicComposition />,
        resource: Resources.TAXONOMIC_COMPARISON,
    },
    communityComparison: {
        name: 'Community Comparison',
        path: '/community-comparison',
        handler: <CommunityComparison />,
        resource: Resources.COMMUNITY_COMPARISON,
    },
};

const EcologyHub = () => {
    const routesConfig = useAppSelector(getInsightRoutesAvailability);

    const { hasResource, isFeatureAvailabilityCheckReady } = useFeature();
    const { isStarterProject } = useProject();
    const habitatInsightsAccess = useHabitatInsightsAccess();
    const isEcologyHubAvailable = hasResource({
        resource: Resources.ECO_STATS,
    });
    const isHabitatInsightsProject = hasResource({
        resource: Resources.HABITAT_INSIGHTS_PAGE,
    });
    const speciesInsightsAccess = useProductAccess(ProductKey.SPECIES_INSIGHTS, isEcologyHubAvailable && !isStarterProject);
    if (!isFeatureAvailabilityCheckReady) {
        return <Loader />;
    }
    if ((!isEcologyHubAvailable && !isHabitatInsightsProject) || isStarterProject) {
        return (
            <AccessGate product={ProductKey.SPECIES_INSIGHTS} access={speciesInsightsAccess}>
                <FreemiumEcologyHub />
            </AccessGate>
        );
    }
    return (
        <Routes>
            {Object.keys(routesConfig).map(routeKey => {
                const isAvailable = routesConfig[routeKey as EcologyHubViews];
                const routeSetting = insightRouteSettings[routeKey as EcologyHubViews];
                const isHabitatView = routeKey === EcologyHubViews.HABITAT_TRENDS;
                const product = isHabitatView ? ProductKey.HABITAT_INSIGHTS : ProductKey.SPECIES_INSIGHTS;
                const access = isHabitatView ? habitatInsightsAccess : speciesInsightsAccess;

                return (
                    <Route
                        path={routeSetting.path}
                        element={
                            <AccessGate product={product} access={access}>
                                <ProtectedRoute isAllowed={isAvailable} redirectPath={'/projects'} routeSetting={routeSetting}>
                                    {routeSetting.handler}
                                </ProtectedRoute>
                            </AccessGate>
                        }
                        key={routeSetting.name}
                    />
                );
            })}
            <Route path='/*' element={<PageNotFound />} />
        </Routes>
    );
};
export default EcologyHub;
